import {
    getBeginningOfDay,
    getBeginningOfNextDay,
    getLatestExemptionLog,
    getTimeLeftInDay,
    IState,
    nonNegativeAvailabilityFn,
    TAvailabilityFn
} from "./helpers";
import { getConfigWorkdayMaximum } from "./hosCalculator";
import { isBigDayExemptionReset, isExemptionAppliedDuringWorkday } from "./exemptionHelpers";
import { applyEmergencyResponseFloor } from "./exemptions/emergencyResponse";
import {
    getCycleDrivingAvailability,
    getCyclePercentDrivingAvailability,
    getDailyDrivingAvailabilityForDay,
    getDailyDutyAvailabilityForDay,
    getDailyOffNeeded,
    getDailyRestNeeded,
    getTotalDailyOffDutyRequired,
    getWorkdayRestAvailabilityForBetweenRestLimit
} from "./availabilityHelpers";
import type { TViolationType } from "../interfaces/statues";

/* The functions in this file calculate availabilities which are "discrete" meaning no other discrete availability was used to in its calculation,
as opposed to the "aggregate" availabilities calculated elsewhere which combine discrete availabilities to calculate the availabilities seen by the driver. */

const getWorkdayEndAvailability: TAvailabilityFn = (state: IState) => {
    let workdayMaximum = getConfigWorkdayMaximum(state);
    if (!state.config.workday || !workdayMaximum) {
        return undefined;
    }
    let workdayAvailability: number,
        controlBigDayLog = getLatestExemptionLog(state.exemption16HLogs, "Exemption16H");

    if (!state.workday.start || !state.workday.end || !state.workday.hasStarted) {
        workdayAvailability = workdayMaximum;
    } else {
        workdayAvailability = state.workday.end.getTime() - state.nextDate.getTime();
    }

    // This is done if most recent record is 16h exemption and there are no logs after it
    if (
        state.config.bigDayBonus &&
        state.workday.end &&
        controlBigDayLog &&
        isExemptionAppliedDuringWorkday(state, controlBigDayLog.dateTime) &&
        isBigDayExemptionReset(state)
    ) {
        state.exemption16HLogs = state.exemption16HLogs.filter(exempt => exempt.id !== controlBigDayLog.id);
        state.cycle.bigDayCount += 1;
        // extend workday by bigDayBonus only if adverse driving is not applied
        if (state.workday.drivingExtended === undefined) {
            workdayAvailability += state.config.bigDayBonus;
            state.workday.workdayExtended = state.workday.workdayExtended
                ? state.workday.workdayExtended + state.config.bigDayBonus
                : state.config.bigDayBonus;
            state.workday.end.setTime(state.workday.end.getTime() + state.config.bigDayBonus);
            state.day.bigDayExemptionUsed = true;
            state.workday.is16HourExemptionApplied = true;
        }
    }
    return workdayAvailability;
};

const getWorkdayDrivingAvailability: TAvailabilityFn = (state: IState) => {
    let driving: number | undefined;
    if (state.workday.driveCount !== undefined && state.workday.drivingLimit !== undefined) {
        driving = state.workday.drivingLimit - state.workday.driveCount;
    } else if (state.config.workday && state.config.workday.driveMaximum !== undefined) {
        driving = state.config.workday.driveMaximum;
    }
    return driving;
};

const getWorkdayDutyAvailability: TAvailabilityFn = (state: IState) => {
    let duty: number | undefined;
    if (state.workday.onDutyCount !== undefined && state.workday.dutyLimit !== undefined) {
        duty = state.workday.dutyLimit - state.workday.onDutyCount;
    } else if (state.config.workday && state.config.workday.dutyMaximum !== undefined) {
        duty = state.config.workday.dutyMaximum;
    }
    return duty;
};

const getWorkdayRestAvailability: TAvailabilityFn = (state: IState) => {
    if (!state.config.rest || !state.config.rest) {
        return undefined;
    }
    if (state.config.rest.onDutyCountTowardRest && state.workday.timeUntilMustRest !== undefined) {
        return state.workday.timeUntilMustRest;
    }
    return getWorkdayRestAvailabilityForBetweenRestLimit(state, state.config.rest.betweenRestLimit);
};

const getCycleDutyAvailability: TAvailabilityFn = (state: IState) => {
    let isSalesPerson = state.config.cycle?.cycleStatuses !== undefined;
    if (isSalesPerson) {
        return undefined;
    }
    // state.cycle.cycleMaximum can be extended by railroad exemption.
    let cycleOnDutyMaximum = state.cycle.cycleMaximum || state.config.cycle?.cycleMaximum;
    if (cycleOnDutyMaximum) {
        return cycleOnDutyMaximum - state.cycle.onDutyCount;
    }
    return undefined;
};

const getCycleEndAvailability: TAvailabilityFn = (state: IState) => {
    let cycleConfig = state.config.cycle;
    if (!cycleConfig || !cycleConfig.nonRolling || !cycleConfig.cycleDays) {
        return undefined;
    }
    if (state.cycle.days > cycleConfig.cycleDays) {
        // Cycle has expired — return negative value (time since expiration)
        let expirationDay = new Date(state.nextDate);
        expirationDay.setDate(expirationDay.getDate() - (state.cycle.days - cycleConfig.cycleDays));
        let expirationDate = getBeginningOfNextDay(expirationDay, state.toLocalTime, state.toUtcTime);
        return expirationDate.getTime() - state.nextDate.getTime();
    }
    // Calculate time remaining until cycle period expires
    let cycleExpirationDay = new Date(state.nextDate);
    cycleExpirationDay.setDate(cycleExpirationDay.getDate() + (cycleConfig.cycleDays - state.cycle.days));
    let cycleExpirationDate = getBeginningOfNextDay(cycleExpirationDay, state.toLocalTime, state.toUtcTime);
    return cycleExpirationDate.getTime() - state.nextDate.getTime();
};

const getCycleRestAvailability: TAvailabilityFn = (state: IState) => {
    let cycleConfig = state.config.cycle;
    if (!cycleConfig) {
        return undefined;
    }
    let cycleRestAvailability: number | undefined = undefined;
    // Canada 25. Must have 24 hours consecutive off in the preceding 14 days.
    // Canada 63. Must have at least 3 periods of off-duty time, each at least 24 hours
    // long, in any period of 24 days, the periods being taken consecutively or separated
    // by on-duty time.
    let cycleRestPeriods = cycleConfig.cycleRestPeriods ?? 1;
    let cycleDays = cycleRestPeriods > 1 ? cycleConfig.cycleDays : cycleConfig.cycleRestDays;
    if (cycleDays && state.cycle.completedRestPeriodDates.length > 0) {
        let nextRestPeriodDue = new Date(state.cycle.completedRestPeriodDates[0].getTime());
        // Cap effectiveLength at cycleRestPeriods: fillRestPeriodsEndDates may extend the array
        // one slot past restPeriods at exact count=N×cycleRestMinimum boundaries (indexPart=0
        // bug), which would change the adjustment term and produce a discontinuous jump in this
        // formula. Clamping prevents the spurious extra element from affecting the result.
        const effectiveLength = Math.min(state.cycle.completedRestPeriodDates.length, cycleRestPeriods);
        nextRestPeriodDue.setDate(nextRestPeriodDue.getDate() + cycleDays - (cycleRestPeriods - (effectiveLength - 1)));
        nextRestPeriodDue = getBeginningOfNextDay(nextRestPeriodDue, state.toLocalTime, state.toUtcTime);
        cycleRestAvailability = nextRestPeriodDue.getTime() - state.nextDate.getTime();
    }
    return cycleRestAvailability;
};

const isCycleDrivingAvailability = (v: number | undefined): v is number => v !== undefined;

const getAggregateCycleDrivingAvailability: TAvailabilityFn = (state: IState) => {
    let cycleDrivingAvailability = getCycleDrivingAvailability(state);
    let cyclePercentDrivingAvailability = getCyclePercentDrivingAvailability(state);
    let cycleDrivingAvailabilities = [cycleDrivingAvailability, cyclePercentDrivingAvailability].filter(isCycleDrivingAvailability);
    return cycleDrivingAvailabilities.length ? Math.min(...cycleDrivingAvailabilities) : undefined;
};

const getCycleRestDutyAvailability: TAvailabilityFn = (state: IState) => {
    let dutyAvailable: number | undefined;
    let onDutyMaximumSince24HourReset = state.config.cycle && state.config.cycle.dutyMaximumSinceCycleRest;
    if (onDutyMaximumSince24HourReset) {
        dutyAvailable = onDutyMaximumSince24HourReset - state.cycle.dutyCountSince24HourRestPeriod;
    }
    return dutyAvailable;
};

const getDailyDrivingAvailability: TAvailabilityFn = (state: IState) => {
    return getDailyDrivingAvailabilityForDay(state.day, state.config);
};

const getDailyDutyAvailability: TAvailabilityFn = (state: IState) => {
    return getDailyDutyAvailabilityForDay(state.day, state.config);
};

const getDailyOffAvailability: TAvailabilityFn = (state: IState) => {
    const offDutyNeeded = getDailyOffNeeded(state);
    // If the user has already completed their off duty requirement for the day then calculate daily off availabilty until the user can go off duty in the next day
    if (offDutyNeeded === 0 && state.config?.daily?.offDutyMinimum) {
        // When today is DayOne, DayTwo's effective requirement is offDutyMin + deferralTimeD2.
        // Subtract deferralTimeD2 so the discrete value decreases at rate −1 through midnight
        // (no discontinuity). Formula mirrors hosCalculator.ts updateDayDeferral (line 274).
        let deferralTimeD2 = 0;
        if (state.day.deferralStatus === "DayOne") {
            const deferralTimeD1 = state.day.deferralTime ?? 0;
            const totalRequired = getTotalDailyOffDutyRequired(state.day, state.config) ?? 0;
            const extraDeferred = Math.max(0, state.day.offDutyCount - totalRequired);
            deferralTimeD2 = deferralTimeD1 > extraDeferred ? deferralTimeD1 - extraDeferred : deferralTimeD1;
        }
        let dayPlusTwo: Date;
        if (state.customCodes.disableDstDailyAvailabilityFix) {
            const t = new Date(state.nextDate);
            t.setDate(t.getDate() + 1);
            dayPlusTwo = getBeginningOfNextDay(t, state.toLocalTime, state.toUtcTime);
        } else {
            dayPlusTwo = getBeginningOfDay(state.nextDate, state.toLocalTime, state.toUtcTime, 2);
        }
        return (
            dayPlusTwo.getTime() -
            state.nextDate.getTime() -
            state.config.daily.offDutyMinimum -
            deferralTimeD2
        );
    }
    return offDutyNeeded === undefined ? undefined : getTimeLeftInDay(state) - offDutyNeeded;
};

const getDailyRestAvailability: TAvailabilityFn = (state: IState) => {
    const restNeeded = getDailyRestNeeded(state);
    // If the user has already completed their daily rest requirement for the day then calculate availability until the user must rest in the next day
    if (restNeeded === 0 && state.config?.rest?.dailyRestMinimum) {
        let dayPlusTwo: Date;
        if (state.customCodes.disableDstDailyAvailabilityFix) {
            const t = new Date(state.nextDate);
            t.setDate(t.getDate() + 1);
            dayPlusTwo = getBeginningOfNextDay(t, state.toLocalTime, state.toUtcTime);
        } else {
            dayPlusTwo = getBeginningOfDay(state.nextDate, state.toLocalTime, state.toUtcTime, 2);
        }
        return (
            dayPlusTwo.getTime() -
            state.nextDate.getTime() -
            state.config.rest.dailyRestMinimum
        );
    }
    return restNeeded === undefined ? undefined : getTimeLeftInDay(state) - restNeeded;
};

/* Each function corresponds to the violation it produces when the result is < 0 and the driver is currently driving, with only the exceptions
"DailyOff" and "DailyRest" which produce violations when the result is < 0 and the day is complete. */
export const discreteAvailabilityFunctions: { [violationType in TViolationType]: TAvailabilityFn } = {
    Workday: nonNegativeAvailabilityFn(getWorkdayEndAvailability),
    Driving: nonNegativeAvailabilityFn(getWorkdayDrivingAvailability),
    Duty: nonNegativeAvailabilityFn(getWorkdayDutyAvailability),
    Rest: nonNegativeAvailabilityFn(getWorkdayRestAvailability),
    Cycle: nonNegativeAvailabilityFn(getCycleDutyAvailability),
    CycleDriving: nonNegativeAvailabilityFn(getAggregateCycleDrivingAvailability),
    CycleRest: nonNegativeAvailabilityFn(getCycleRestAvailability),
    CycleEnd: nonNegativeAvailabilityFn(getCycleEndAvailability),
    DutySinceCycleRest: nonNegativeAvailabilityFn(getCycleRestDutyAvailability),
    DailyDriving: nonNegativeAvailabilityFn(getDailyDrivingAvailability),
    DailyDuty: nonNegativeAvailabilityFn(getDailyDutyAvailability),
    DailyOff: nonNegativeAvailabilityFn(getDailyOffAvailability),
    DailyRest: nonNegativeAvailabilityFn(getDailyRestAvailability)
};

export type TDiscreteAvailabilitySummary = { [violationType in TViolationType]?: number | undefined };
export const getDiscreteAvailabilitySummary = (state: IState, enforceNonNegative = false): TDiscreteAvailabilitySummary => {
    let result: TDiscreteAvailabilitySummary = {};
    for (let violationType of Object.keys(discreteAvailabilityFunctions) as TViolationType[]) {
        let value = discreteAvailabilityFunctions[violationType](state, enforceNonNegative);
        if (value !== undefined) {
            result[violationType] = value;
        }
    }
    // MYG-139401: flooring the discrete values carries into every aggregate the UI builds from
    // them, because min(max(T, a), max(T, b)) === max(T, min(a, b)).
    applyEmergencyResponseFloor(state, result);
    return result;
};
